import { computed, reactive, readonly } from 'vue'

// Dispositivos de áudio do telefone no navegador (PhoneWidget.vue / useBrowserPhone.ts): microfone, saída da conversa
// e saída do toque — escolhidos e testados pelo agente. Fica no navegador (localStorage): o id de cada dispositivo
// é por computador, não faz sentido ir pro servidor. Vazio = o padrão do sistema operacional.
//
// - Microfone: o SDK da Yeastar lê phone.deviceIds.microphoneId a cada ligação/atendimento — a troca vale a partir
//   da próxima chamada, sem reconectar o ramal.
// - Saídas: HTMLMediaElement.setSinkId (Chrome, Edge, Firefox 116+; Safari não tem) — sem suporte, fica no padrão.

export interface AudioDeviceChoice {
  microphoneId: string
  speakerId: string
  ringerId: string
}

export interface DeviceOption {
  id: string
  label: string
}

const STORAGE_KEY = 'phone.audioDevices'
const EMPTY: AudioDeviceChoice = { microphoneId: '', speakerId: '', ringerId: '' }

function loadChoice(): AudioDeviceChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : { ...EMPTY }
  } catch {
    return { ...EMPTY }
  }
}

const state = reactive({
  choice: loadChoice(),
  inputs: [] as DeviceOption[],
  outputs: [] as DeviceOption[],
  /** O navegador só mostra o nome dos dispositivos depois de liberar o microfone uma vez. */
  labelsVisible: false,
  problem: null as string | null,
})

let listening = false

export const supportsOutputSelection =
  typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype

/** Lista os dispositivos. askPermission = pede o microfone antes (é o que libera os nomes). */
export async function refreshDevices(askPermission = false): Promise<void> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  if (!md?.enumerateDevices) {
    state.problem = 'Este navegador não deixa escolher dispositivos de áudio.'
    return
  }
  if (askPermission) {
    try {
      const stream = await md.getUserMedia({ audio: true })
      stream.getTracks().forEach(t => t.stop())
      state.problem = null
    } catch {
      state.problem = 'O navegador não liberou o microfone. Permita o microfone pra este site e tente de novo.'
    }
  }
  const devices = await md.enumerateDevices()
  state.labelsVisible = devices.some(d => !!d.label)
  state.inputs = toOptions(devices.filter(d => d.kind === 'audioinput'), 'Microfone')
  state.outputs = toOptions(devices.filter(d => d.kind === 'audiooutput'), 'Saída')
  if (!listening && md.addEventListener) {
    listening = true
    md.addEventListener('devicechange', () => { void refreshDevices() })
  }
}

/** "default"/"communications" são aliases do Windows pro padrão — a opção "Padrão do sistema" já cobre. */
export function toOptions(devices: Array<Pick<MediaDeviceInfo, 'deviceId' | 'label'>>, fallback: string): DeviceOption[] {
  return devices
    .filter(d => d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications')
    .map((d, i) => ({ id: d.deviceId, label: d.label || `${fallback} ${i + 1}` }))
}

export function setDeviceChoice(patch: Partial<AudioDeviceChoice>): void {
  Object.assign(state.choice, patch)
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.choice)) } catch { /* modo privado: vale só nesta aba */ }
}

/** Id escolhido se ainda estiver conectado; senão '' (padrão). Lista vazia = ainda não listou, confia na escolha. */
export function resolveDevice(id: string, options: DeviceOption[]): string {
  if (!id || !options.length) return id
  return options.some(o => o.id === id) ? id : ''
}

/** Toca a saída num <audio> escolhido. Sem suporte ou dispositivo sumido: fica no padrão, sem erro. */
export async function applySink(el: HTMLMediaElement | null, deviceId: string): Promise<void> {
  if (!el || !supportsOutputSelection) return
  try {
    await (el as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId || '')
  } catch {
    try { await (el as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId('') } catch { /* fica onde está */ }
  }
}

/** Ids efetivos (o escolhido se ainda conectado, senão o padrão). */
export const effectiveDevices = computed<AudioDeviceChoice>(() => ({
  microphoneId: resolveDevice(state.choice.microphoneId, state.inputs),
  speakerId: resolveDevice(state.choice.speakerId, state.outputs),
  ringerId: resolveDevice(state.choice.ringerId, state.outputs),
}))

/** Escolheu um dispositivo que não está conectado agora (headset desligado): a tela avisa. */
export const missingDevices = computed(() => {
  const missing: string[] = []
  if (state.choice.microphoneId && state.inputs.length && !effectiveDevices.value.microphoneId) missing.push('microfone')
  if (state.choice.speakerId && state.outputs.length && !effectiveDevices.value.speakerId) missing.push('saída da conversa')
  if (state.choice.ringerId && state.outputs.length && !effectiveDevices.value.ringerId) missing.push('saída do toque')
  return missing
})

// ── Testes ────────────────────────────────────────────────────────────────────────────────────

/** Liga o microfone escolhido e chama onLevel (0–1) ~20x por segundo até stop(). */
export async function startMicTest(deviceId: string, onLevel: (level: number) => void): Promise<() => void> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true })
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  const ctx = new Ctx()
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  ctx.createMediaStreamSource(stream).connect(analyser)
  const data = new Uint8Array(analyser.fftSize)
  const timer = setInterval(() => {
    analyser.getByteTimeDomainData(data)
    let peak = 0
    for (const v of data) peak = Math.max(peak, Math.abs(v - 128))
    onLevel(Math.min(1, peak / 64))
  }, 50)
  return () => {
    clearInterval(timer)
    stream.getTracks().forEach(t => t.stop())
    void ctx.close().catch(() => undefined)
    onLevel(0)
  }
}

/** Toca um som curto na saída escolhida. kind 'ring' = os bipes do toque; 'tone' = um tom de teste. */
export async function playTestSound(deviceId: string, kind: 'tone' | 'ring'): Promise<void> {
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  const ctx = new Ctx()
  const dest = ctx.createMediaStreamDestination()
  const beeps = kind === 'ring' ? [0, 0.25, 1, 1.25] : [0]
  for (const at of beeps) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const len = kind === 'ring' ? 0.2 : 0.8
    osc.frequency.value = kind === 'ring' ? 440 : 660
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + at)
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + len)
    osc.connect(gain).connect(dest)
    osc.start(ctx.currentTime + at)
    osc.stop(ctx.currentTime + at + len + 0.02)
  }
  const el = new Audio()
  el.srcObject = dest.stream
  await applySink(el, deviceId)
  await el.play().catch(() => undefined)
  setTimeout(() => { el.pause(); el.srcObject = null; void ctx.close().catch(() => undefined) }, 1800)
}

export function useAudioDevices() {
  return { devices: readonly(state) }
}
